import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getRegistros, getFuncionariosResumo, getDepartamentos, getSaldosHora, getFuncionario, getJornadaById } from '../services/api';

const DIAS_PT = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const decToHHMM = v => {
  if (v == null) return '—';
  const neg = v < 0;
  const abs = Math.abs(v);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${neg?'-':''}${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
};

const isoToBR = iso => { if(!iso||!iso.includes('-')) return iso; const[y,mn,d]=iso.split('-'); return `${d}/${mn}/${y}`; };
const brToISO = br => { if(!br||!br.includes('/')) return br; const[d,mn,y]=br.split('/'); return `${y}-${mn}-${d}`; };
const diasNoMes = (ano, mes) => new Date(ano, mes, 0).getDate();

const STATUS_COR = {
  NORMAL:   { bg:'#dcfce7', color:'#166534' },
  FALTA:    { bg:'#fee2e2', color:'#991b1b' },
  ATESTADO: { bg:'#fef9c3', color:'#854d0e' },
  FOLGA:    { bg:'#f1f5f9', color:'#475569' },
  FERIAS:   { bg:'#dbeafe', color:'#1e40af' },
  FERIADO:  { bg:'#d1fae5', color:'#065f46' },
};

export default function PontoIndividual() {
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth() + 1;
  const location = useLocation();
  const navState = location.state || {};

  const [funcs, setFuncs]               = useState([]);
  const [deptos, setDeptos]             = useState([]);
  const [filtroDep, setFiltroDep]       = useState('');
  const [busca, setBusca]               = useState('');
  const [funcSel, setFuncSel]           = useState(null);
  const [dropOpen, setDropOpen]         = useState(false);
  const [mes, setMes]                   = useState(navState.mes || mesAtual);
  const [ano, setAno]                   = useState(navState.ano || anoAtual);
  const [registros, setRegistros]       = useState([]);
  const [compPorData, setCompPorData]   = useState({});
  const [jornadaEntrada, setJornadaEntrada] = useState('');
  const [loading, setLoading]           = useState(false);
  const [pdfLoading, setPdfLoading]     = useState(false);

  const inputRef = useRef();
  const dropRef  = useRef();

  useEffect(() => {
    getFuncionariosResumo().then(r => {
      const lista = r.data;
      setFuncs(lista);
      if (navState.funcionarioId) {
        const f = lista.find(x => String(x.id) === String(navState.funcionarioId));
        if (f) { setFuncSel(f); setBusca(f.nome); }
      }
    });
  }, []); // eslint-disable-line
  useEffect(() => { getDepartamentos({ ativo: true }).then(r => setDeptos(r.data.results || r.data)).catch(()=>{}); }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const funcsFiltradas = funcs.filter(f => {
    if (filtroDep && String(f.departamento) !== String(filtroDep)) return false;
    if (!busca.trim()) return true;
    const q = busca.toLowerCase();
    return f.nome.toLowerCase().includes(q) || (f.matricula || '').toLowerCase().includes(q);
  });

  const selecionarFunc = (f) => {
    setFuncSel(f);
    setBusca(f.nome);
    setDropOpen(false);
  };

  const limparSelecao = () => {
    setFuncSel(null);
    setBusca('');
    setRegistros([]);
    inputRef.current?.focus();
  };

  const carregar = useCallback(async () => {
    if (!funcSel) return;
    setLoading(true);
    try {
      const dataInicio = `${ano}-${String(mes).padStart(2,'0')}-01`;
      const dataFim    = `${ano}-${String(mes).padStart(2,'0')}-${String(diasNoMes(ano, mes)).padStart(2,'0')}`;
      // API espera data em BR
      const r = await getRegistros({ funcionario: funcSel.id, data_inicio: isoToBR(dataInicio), data_fim: isoToBR(dataFim), page_size: 100 });
      setRegistros(r.data.results || r.data);

      // Compensações aprovadas para este funcionário
      const sc = await getSaldosHora({ tipo: 'COMPENSAR_DEPOIS', page_size: 500 });
      const lista = sc.data.results || sc.data;
      const mapa = {};
      lista.forEach(s => {
        if (s.autorizado !== true) return;
        if (String(s.registro_funcionario_id) !== String(funcSel.id)) return;
        (s.compensacoes || []).forEach(c => {
          if (!c.data) return;
          mapa[c.data] = (mapa[c.data] || 0) + (parseInt(c.minutos) || 0);
        });
      });
      setCompPorData(mapa);

      // Busca horário de entrada previsto da jornada do funcionário
      try {
        const funcData = (await getFuncionario(funcSel.id)).data;
        if (funcData.jornada) {
          const jornData = (await getJornadaById(funcData.jornada)).data;
          let entradaPrev = jornData.hora_entrada ? String(jornData.hora_entrada).slice(0, 5) : '';
          if (!entradaPrev && jornData.horarios_dias) {
            for (const sig of ['SEG','TER','QUA','QUI','SEX']) {
              if (jornData.horarios_dias[sig]?.entrada) {
                entradaPrev = String(jornData.horarios_dias[sig].entrada).slice(0, 5);
                break;
              }
            }
          }
          setJornadaEntrada(entradaPrev);
        } else {
          setJornadaEntrada('');
        }
      } catch { setJornadaEntrada(''); }
    } finally { setLoading(false); }
  }, [funcSel, mes, ano]);

  useEffect(() => { carregar(); }, [carregar]);

  // Monta grade completa do mês (todos os dias, com ou sem registro)
  const grade = (() => {
    const total = diasNoMes(ano, mes);
    const regPorDia = {};
    registros.forEach(r => {
      const iso = brToISO(r.data);
      if (iso) regPorDia[iso] = r;
    });
    const dias = [];
    for (let d = 1; d <= total; d++) {
      const iso = `${ano}-${String(mes).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const dt  = new Date(iso + 'T00:00:00');
      dias.push({ iso, diaSem: DIAS_PT[dt.getDay()], reg: regPorDia[iso] || null });
    }
    return dias;
  })();

  // Totais
  const safe = v => (v != null && !isNaN(Number(v))) ? Number(v) : 0;
  const totais = grade.reduce((acc, { reg }) => {
    if (!reg) return acc;
    acc.trab  += safe(reg.horas_trabalhadas);
    acc.extra += safe(reg.horas_extras);
    acc.saldo += safe(reg.saldo_horas);
    return acc;
  }, { trab: 0, extra: 0, saldo: 0 });

  const ocorrencias = registros.reduce((acc, r) => {
    if (r.entrada_atrasada) acc.entrada_atrasada++;
    if (r.almoco_tardio)    acc.almoco_tardio++;
    if (r.almoco_precoce)   acc.almoco_precoce++;
    if (r.almoco_curto)     acc.almoco_curto++;
    if (r.saida_apos_1815)  acc.saida_apos_1815++;
    return acc;
  }, { entrada_atrasada: 0, almoco_tardio: 0, almoco_precoce: 0, almoco_curto: 0, saida_apos_1815: 0 });
  const totalOcorrencias = Object.values(ocorrencias).reduce((s, v) => s + v, 0);

  const OCORR_DEF = [
    { key: 'entrada_atrasada', icon: 'bi-alarm-fill',                color: '#ca8a04', title: 'Entrada atrasada' },
    { key: 'almoco_tardio',    icon: 'bi-exclamation-triangle-fill', color: '#f97316', title: 'Almoço tardio' },
    { key: 'almoco_precoce',   icon: 'bi-clock-fill',                color: '#3b82f6', title: 'Almoço precoce' },
    { key: 'almoco_curto',     icon: 'bi-cup-hot-fill',              color: '#a855f7', title: 'Almoço curto' },
    { key: 'saida_apos_1815',  icon: 'bi-moon-stars-fill',           color: '#dc2626', title: 'Saída após 18h15' },
  ];

  const anos = [];
  for (let y = anoAtual - 3; y <= anoAtual + 1; y++) anos.push(y);

  const btnBase = { display:'inline-flex', alignItems:'center', gap:4, borderRadius:6, padding:'3px 9px', cursor:'pointer', fontSize:'0.72rem', fontWeight:600 };

  // ── Helper: pré-renderiza um Bootstrap Icon como PNG data URL ──
  const renderIcon = async (iconClass, colorHex, sizePx = 24) => {
    await document.fonts.load(`${sizePx}px "bootstrap-icons"`).catch(() => {});
    const canvas = document.createElement('canvas');
    canvas.width = sizePx; canvas.height = sizePx;
    const ctx = canvas.getContext('2d');
    const el = document.createElement('i');
    el.className = `bi ${iconClass}`;
    Object.assign(el.style, { visibility: 'hidden', position: 'fixed', fontSize: `${sizePx}px` });
    document.body.appendChild(el);
    let char = window.getComputedStyle(el, '::before').content.replace(/["']/g, '');
    document.body.removeChild(el);
    ctx.fillStyle = colorHex;
    ctx.font = `${sizePx}px "bootstrap-icons"`;
    ctx.textBaseline = 'middle';
    ctx.fillText(char, 0, sizePx / 2);
    return canvas.toDataURL('image/png');
  };

  // ── Helper: pré-renderiza todos os ícones usados no PDF ──
  const preRenderIconPngs = async () => {
    const iconSrcDefs = [
      { key: 'entrada_atrasada', cls: 'bi-alarm-fill',                hex: '#ca8a04' },
      { key: 'almoco_tardio',    cls: 'bi-exclamation-triangle-fill', hex: '#f97316' },
      { key: 'almoco_precoce',   cls: 'bi-clock-fill',                hex: '#3b82f6' },
      { key: 'almoco_curto',     cls: 'bi-cup-hot-fill',              hex: '#a855f7' },
      { key: 'saida_apos_1815',  cls: 'bi-moon-stars-fill',           hex: '#dc2626' },
      { key: 'per_curto',        cls: 'bi-arrow-down-circle-fill',    hex: '#3b82f6' },
      { key: 'per_longo',        cls: 'bi-arrow-up-circle-fill',      hex: '#f97316' },
      { key: 'alm_curto',        cls: 'bi-cup-hot-fill',              hex: '#a855f7' },
      { key: 'alm_longo',        cls: 'bi-hourglass-split',           hex: '#f97316' },
    ];
    const iconPngs = {};
    await Promise.all(iconSrcDefs.map(async d => { iconPngs[d.key] = await renderIcon(d.cls, d.hex, 24); }));
    return iconPngs;
  };

  // ── Renderiza uma página de funcionário no doc jsPDF ──
  // params: { funcSel, grade, compPorData, jornadaEntrada, ocorrencias, totalOcorrencias, mes, ano, iconPngs, addPage }
  const renderFuncPage = (doc, { funcSel: fs, grade: gr, compPorData: cpd, jornadaEntrada: je, ocorrencias: ocorr, totalOcorrencias: totOcorr, mes: m, ano: a, iconPngs, addPage }) => {
    if (addPage) doc.addPage();

    const PW = doc.internal.pageSize.getWidth();

    // ── Cabeçalho compacto ──
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, PW, 1.5, 'F');
    doc.setFillColor(248, 250, 252);
    doc.rect(0, 1.5, PW, 16, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(0, 17.5, PW, 17.5);

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    const col1X = PW * 0.38;
    const col2X = PW * 0.72;
    doc.line(col1X, 3, col1X, 16);
    doc.line(col2X, 3, col2X, 16);

    // Esquerda — título
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text('CARTÃO DE PONTO INDIVIDUAL', 14, 9);
    doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('Gestão de Pessoal · Controle de Ponto', 14, 14.5);

    // Centro — funcionário
    const cx = (col1X + col2X) / 2;
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(fs.nome, cx, 9, { align: 'center' });
    doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`${MESES_PT[m - 1]}  /  ${a}`, cx, 14.5, { align: 'center' });

    // Direita — entrada prevista + gerado em
    const rx = col2X + (PW - col2X) / 2;
    if (je) {
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
      doc.text('Entrada prevista', rx, 8, { align: 'center' });
      doc.setTextColor(37, 99, 235);
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.text(je, rx, 13.5, { align: 'center' });
    }
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(5.5); doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, rx, je ? 17 : 12, { align: 'center' });

    // ── Tabela de registros ──
    const cols = [
      { header: 'Data',   dataKey: 'data' },
      { header: 'Dia',    dataKey: 'dia' },
      { header: 'Ent.1',  dataKey: 'e1' },
      { header: 'Saí.1',  dataKey: 's1' },
      { header: 'Ent.2',  dataKey: 'e2' },
      { header: 'Saí.2',  dataKey: 's2' },
      { header: 'Ent.3',  dataKey: 'e3' },
      { header: 'Saí.3',  dataKey: 's3' },
      { header: 'Ent.4',  dataKey: 'e4' },
      { header: 'Saí.4',  dataKey: 's4' },
      { header: 'Trab.',  dataKey: 'trab' },
      { header: 'Comp.',  dataKey: 'comp' },
      { header: 'Extra',  dataKey: 'extra' },
      { header: 'Saldo',  dataKey: 'saldo' },
      { header: 'Per.',   dataKey: 'per' },
      { header: 'Alm.',   dataKey: 'alm' },
      { header: 'Status', dataKey: 'status' },
    ];

    const rows = gr.map(({ iso, diaSem, reg }) => {
      const [yy, mm, dd] = iso.split('-');
      const toM = t => { if (!t) return null; const [h, mn] = t.slice(0,5).split(':').map(Number); return h*60+mn; };
      const fmtDiff = d => d > 0 ? `${String(Math.floor(d/60)).padStart(2,'0')}:${String(d%60).padStart(2,'0')}` : '—';
      const perDiff = (() => { const e1 = toM(reg?.hora_entrada); const s1 = toM(reg?.hora_saida_almoco); return (e1 !== null && s1 !== null) ? s1 - e1 : null; })();
      const almDiff = (() => { const s1 = toM(reg?.hora_saida_almoco); const e2 = toM(reg?.hora_retorno_almoco); return (s1 !== null && e2 !== null) ? e2 - s1 : null; })();
      const per = perDiff !== null && perDiff > 0 ? fmtDiff(perDiff) : '—';
      const alm = almDiff !== null && almDiff > 0 ? fmtDiff(almDiff) : '—';
      return {
        data:   `${dd}/${mm}/${yy}`,
        dia:    diaSem,
        e1:     reg?.hora_entrada        ? reg.hora_entrada.slice(0,5)        : '—',
        s1:     reg?.hora_saida_almoco   ? reg.hora_saida_almoco.slice(0,5)   : '—',
        e2:     reg?.hora_retorno_almoco ? reg.hora_retorno_almoco.slice(0,5) : '—',
        s2:     reg?.hora_saida          ? reg.hora_saida.slice(0,5)          : '—',
        e3:     reg?.hora_entrada3       ? reg.hora_entrada3.slice(0,5)       : '—',
        s3:     reg?.hora_saida3         ? reg.hora_saida3.slice(0,5)         : '—',
        e4:     reg?.hora_entrada4       ? reg.hora_entrada4.slice(0,5)       : '—',
        s4:     reg?.hora_saida4         ? reg.hora_saida4.slice(0,5)         : '—',
        trab:   reg?.horas_trabalhadas != null ? decToHHMM(reg.horas_trabalhadas) : '—',
        comp:   cpd[iso] ? decToHHMM(cpd[iso] / 60) : '—',
        extra:  reg?.horas_extras != null && reg.horas_extras !== 0 ? `${reg.horas_extras > 0 ? '+' : ''}${decToHHMM(reg.horas_extras)}` : '—',
        saldo:  reg?.saldo_horas != null && Math.round(Math.abs(reg.saldo_horas)*60) > 10 ? `${reg.saldo_horas > 0 ? '+' : ''}${decToHHMM(reg.saldo_horas)}` : '—',
        per,
        alm,
        status: reg?.status_display || (diaSem === 'Sáb' || diaSem === 'Dom' ? 'FDS' : ''),
        _reg:      reg,
        _dia:      diaSem,
        _perDiff:  perDiff,
        _almDiff:  almDiff,
      };
    });

    const occDefs = [
      { key: 'entrada_atrasada', label: 'Entrada Atrasada', count: ocorr.entrada_atrasada, color: [202, 138,   4], bg: [254, 249, 195] },
      { key: 'almoco_tardio',    label: 'Almoco Tardio',   count: ocorr.almoco_tardio,    color: [249, 115,  22], bg: [255, 237, 213] },
      { key: 'almoco_precoce',   label: 'Almoco Precoce',  count: ocorr.almoco_precoce,   color: [ 59, 130, 246], bg: [219, 234, 254] },
      { key: 'almoco_curto',     label: 'Almoco Curto',    count: ocorr.almoco_curto,     color: [168,  85, 247], bg: [243, 232, 255] },
      { key: 'saida_apos_1815',  label: 'Saida > 18h15',  count: ocorr.saida_apos_1815,  color: [220,  38,  38], bg: [254, 226, 226] },
    ];

    autoTable(doc, {
      startY: 21,
      columns: cols,
      body: rows,
      theme: 'plain',
      styles: { fontSize: 6.5, cellPadding: 1.2, valign: 'middle', textColor: [30, 41, 59] },
      headStyles: { fillColor: [248, 250, 252], textColor: [100, 116, 139], fontStyle: 'bold', fontSize: 6.5, lineWidth: 0.1, lineColor: [226, 232, 240] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0:  { cellWidth: 18 },
        1:  { cellWidth: 9, halign: 'center' },
        2:  { cellWidth: 18, halign: 'center', textColor: [5, 150, 105] },
        3:  { cellWidth: 18, halign: 'center', textColor: [220, 38, 38] },
        4:  { cellWidth: 18, halign: 'center', textColor: [5, 150, 105] },
        5:  { cellWidth: 18, halign: 'center', textColor: [220, 38, 38] },
        6:  { cellWidth: 18, halign: 'center', textColor: [5, 150, 105] },
        7:  { cellWidth: 18, halign: 'center', textColor: [220, 38, 38] },
        8:  { cellWidth: 18, halign: 'center', textColor: [5, 150, 105] },
        9:  { cellWidth: 18, halign: 'center', textColor: [220, 38, 38] },
        10: { cellWidth: 14, halign: 'center', fontStyle: 'bold', textColor: [37, 99, 235] },
        11: { cellWidth: 12, halign: 'center', textColor: [3, 105, 161] },
        12: { cellWidth: 12, halign: 'center', textColor: [124, 58, 237] },
        13: { cellWidth: 12, halign: 'center' },
        14: { cellWidth: 15, halign: 'center', textColor: [8, 145, 178] },
        15: { cellWidth: 15, halign: 'center', textColor: [168, 85, 247] },
        16: { cellWidth: 18, halign: 'center' },
      },
      didParseCell(data) {
        const row = data.row.raw;
        if (data.section !== 'body' || !row?._reg || !row?._dia) return;
        const reg = row._reg;
        const fds = row._dia === 'Sáb' || row._dia === 'Dom';
        if (fds) { data.cell.styles.fillColor = [250, 251, 255]; data.cell.styles.textColor = [148, 163, 184]; }
        if (data.column.index === 2 && reg.entrada_atrasada)  data.cell.styles.fillColor = [254, 249, 195];
        if (data.column.index === 3 && reg.almoco_tardio)     data.cell.styles.fillColor = [255, 237, 213];
        if (data.column.index === 3 && reg.almoco_precoce)    data.cell.styles.fillColor = [219, 234, 254];
        if (data.column.index === 4 && reg.almoco_curto)      data.cell.styles.fillColor = [243, 232, 255];
        if (data.column.index === 5 && reg.saida_apos_1815)   data.cell.styles.fillColor = [254, 226, 226];
        // Per. (col 14): azul se < 4h, laranja se > 5h
        if (data.column.index === 14 && row._perDiff !== null && !fds) {
          if (row._perDiff < 240)      { data.cell.styles.textColor = [59, 130, 246];  data.cell.styles.fillColor = [219, 234, 254]; }
          else if (row._perDiff > 300) { data.cell.styles.textColor = [194, 65, 12];   data.cell.styles.fillColor = [255, 237, 213]; }
        }
        // Alm. (col 15): roxo se < 1h, laranja se > 1h
        if (data.column.index === 15 && row._almDiff !== null && !fds) {
          if (row._almDiff < 60)      { data.cell.styles.textColor = [126, 34, 206];  data.cell.styles.fillColor = [243, 232, 255]; }
          else if (row._almDiff > 65) { data.cell.styles.textColor = [194, 65, 12];   data.cell.styles.fillColor = [255, 237, 213]; }
        }
      },
      didDrawCell(data) {
        if (data.section !== 'body') return;
        const row = data.row.raw;
        if (!row?._reg) return;
        const reg = row._reg;
        const idx = data.column.index;
        let key = null;
        if (idx === 2 && reg.entrada_atrasada)                      key = 'entrada_atrasada';
        if (idx === 3 && reg.almoco_tardio)                         key = 'almoco_tardio';
        if (idx === 3 && reg.almoco_precoce && !reg.almoco_tardio)  key = 'almoco_precoce';
        if (idx === 4 && reg.almoco_curto)                          key = 'almoco_curto';
        if (idx === 5 && reg.saida_apos_1815)                       key = 'saida_apos_1815';
        if (idx === 14 && row._perDiff !== null) {
          if (row._perDiff < 240)      key = 'per_curto';
          else if (row._perDiff > 300) key = 'per_longo';
        }
        if (idx === 15 && row._almDiff !== null) {
          if (row._almDiff < 60)      key = 'alm_curto';
          else if (row._almDiff > 65) key = 'alm_longo';
        }
        if (key && iconPngs[key]) {
          const iSz = 3;
          const timeStr = (data.cell.text || [])[0] || '';
          doc.setFontSize(6.5);
          const tw = doc.getTextWidth(timeStr);
          const textX = data.cell.x + (data.cell.width - tw) / 2;
          const iconX = textX - iSz - 0.8;
          const iconY = data.cell.y + (data.cell.height - iSz) / 2;
          doc.addImage(iconPngs[key], 'PNG', iconX, iconY, iSz, iSz);
        }
      },
      didDrawPage() {
        const pg  = doc.internal.getCurrentPageInfo().pageNumber;
        const tot = doc.internal.getNumberOfPages();
        const PH  = doc.internal.pageSize.getHeight();
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(148, 163, 184);
        doc.text(`${fs.nome} · ${MESES_PT[m-1]}/${a}`, 14, PH - 5);
        doc.text(`Pag. ${pg}/${tot}`, PW - 14, PH - 5, { align: 'right' });
      },
    });

    // ── Bloco de ocorrências (após a tabela) ──
    const sy   = doc.lastAutoTable.finalY + 4;
    const bx   = 14;
    const bw   = PW - 28;
    const bh   = 13;
    const titW = 32;
    const totW = 26;
    const occW = (bw - titW - totW) / occDefs.length;

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.roundedRect(bx, sy, bw, bh, 1.5, 1.5, 'FD');

    doc.setFillColor(239, 246, 255);
    doc.rect(bx, sy, titW, bh, 'F');
    doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 64, 175);
    doc.text('OCORRENCIAS', bx + titW / 2, sy + 4.5, { align: 'center' });
    doc.text('DO MES',       bx + titW / 2, sy + 8.5, { align: 'center' });

    occDefs.forEach((o, i) => {
      const ox = bx + titW + i * occW;
      doc.setFillColor(...o.color);
      doc.rect(ox, sy, occW, 2, 'F');
      doc.setFillColor(...o.bg);
      doc.rect(ox, sy + 2, occW, bh - 2, 'F');
      doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.3);
      doc.line(ox, sy, ox, sy + bh);

      const iSz = 5;
      const iconX = ox + 3;
      const iconY = sy + (bh - iSz) / 2;
      const textX = ox + 3 + iSz + 2;
      const textW = occW - iSz - 7;

      if (iconPngs[o.key]) {
        doc.addImage(iconPngs[o.key], 'PNG', iconX, iconY, iSz, iSz);
      }
      doc.setFontSize(5.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139);
      doc.text(o.label, textX, sy + 5.5, { maxWidth: textW });
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...o.color);
      doc.text(o.count > 0 ? String(o.count) : '0', textX, sy + 11);
    });

    const tx = bx + titW + occDefs.length * occW;
    doc.setFillColor(239, 246, 255);
    doc.rect(tx, sy, totW, bh, 'F');
    doc.setDrawColor(226, 232, 240); doc.line(tx, sy, tx, sy + bh);
    doc.setFontSize(5.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139);
    doc.text('TOTAL', tx + totW / 2, sy + 5, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 64, 175);
    doc.text(String(totOcorr), tx + totW / 2, sy + 11, { align: 'center' });
  };

  const gerarPDF = async () => {
    const iconPngs = await preRenderIconPngs();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    renderFuncPage(doc, {
      funcSel, grade, compPorData, jornadaEntrada,
      ocorrencias, totalOcorrencias,
      mes, ano, iconPngs, addPage: false,
    });
    doc.save(`ponto_${funcSel.nome.split(' ')[0].toLowerCase()}_${String(mes).padStart(2,'0')}_${ano}.pdf`);
  };

  const gerarPDFSetor = async () => {
    const nomeSetor = deptos.find(d => String(d.id) === String(filtroDep))?.nome || String(filtroDep);
    const funcSetor = funcs.filter(f => String(f.departamento) === String(filtroDep));
    if (funcSetor.length === 0) return;

    setPdfLoading(true);
    try {
      const iconPngs = await preRenderIconPngs();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

      const dataInicio = `${ano}-${String(mes).padStart(2,'0')}-01`;
      const dataFim    = `${ano}-${String(mes).padStart(2,'0')}-${String(diasNoMes(ano, mes)).padStart(2,'0')}`;

      for (let fi = 0; fi < funcSetor.length; fi++) {
        const f = funcSetor[fi];

        // Registros do funcionário
        const rRegs = await getRegistros({ funcionario: f.id, data_inicio: isoToBR(dataInicio), data_fim: isoToBR(dataFim), page_size: 100 });
        const regsF = rRegs.data.results || rRegs.data;

        // Compensações
        const sc = await getSaldosHora({ tipo: 'COMPENSAR_DEPOIS', page_size: 500 });
        const listaComp = sc.data.results || sc.data;
        const compPorDataF = {};
        listaComp.forEach(s => {
          if (s.autorizado !== true) return;
          if (String(s.registro_funcionario_id) !== String(f.id)) return;
          (s.compensacoes || []).forEach(c => {
            if (!c.data) return;
            compPorDataF[c.data] = (compPorDataF[c.data] || 0) + (parseInt(c.minutos) || 0);
          });
        });

        // Jornada
        let jornadaEntradaF = '';
        try {
          const funcData = (await getFuncionario(f.id)).data;
          if (funcData.jornada) {
            const jornData = (await getJornadaById(funcData.jornada)).data;
            let ep = jornData.hora_entrada ? String(jornData.hora_entrada).slice(0, 5) : '';
            if (!ep && jornData.horarios_dias) {
              for (const sig of ['SEG','TER','QUA','QUI','SEX']) {
                if (jornData.horarios_dias[sig]?.entrada) {
                  ep = String(jornData.horarios_dias[sig].entrada).slice(0, 5);
                  break;
                }
              }
            }
            jornadaEntradaF = ep;
          }
        } catch { jornadaEntradaF = ''; }

        // Grade do mês
        const total = diasNoMes(ano, mes);
        const regPorDia = {};
        regsF.forEach(r => { const iso = brToISO(r.data); if (iso) regPorDia[iso] = r; });
        const gradeF = [];
        for (let d = 1; d <= total; d++) {
          const iso = `${ano}-${String(mes).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const dt  = new Date(iso + 'T00:00:00');
          gradeF.push({ iso, diaSem: DIAS_PT[dt.getDay()], reg: regPorDia[iso] || null });
        }

        // Ocorrências
        const ocorrF = regsF.reduce((acc, r) => {
          if (r.entrada_atrasada) acc.entrada_atrasada++;
          if (r.almoco_tardio)    acc.almoco_tardio++;
          if (r.almoco_precoce)   acc.almoco_precoce++;
          if (r.almoco_curto)     acc.almoco_curto++;
          if (r.saida_apos_1815)  acc.saida_apos_1815++;
          return acc;
        }, { entrada_atrasada: 0, almoco_tardio: 0, almoco_precoce: 0, almoco_curto: 0, saida_apos_1815: 0 });
        const totalOcorrF = Object.values(ocorrF).reduce((s, v) => s + v, 0);

        renderFuncPage(doc, {
          funcSel: f,
          grade: gradeF,
          compPorData: compPorDataF,
          jornadaEntrada: jornadaEntradaF,
          ocorrencias: ocorrF,
          totalOcorrencias: totalOcorrF,
          mes, ano, iconPngs,
          addPage: fi > 0,
        });
      }

      doc.save(`ponto_setor_${nomeSetor.replace(/\s+/g, '_').toLowerCase()}_${String(mes).padStart(2,'0')}_${ano}.pdf`);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingBottom:16, marginBottom:20, borderBottom:'1px solid #f1f5f9' }}>
        <div style={{ display:'flex', alignItems:'center' }}>
          <div style={{ width:38, height:38, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--primary)', fontSize:'1rem', marginRight:14, flexShrink:0 }}>
            <i className="bi bi-person-badge-fill" />
          </div>
          <div>
            <div style={{ fontSize:'0.62rem', fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:3 }}>Gestão de Pessoal · Controle de Ponto</div>
            <div style={{ fontSize:'1.3rem', fontWeight:700, color:'#334155', letterSpacing:'-0.5px', lineHeight:1.2 }}>Ponto Individual</div>
          </div>
        </div>
        {funcSel && (
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {OCORR_DEF.map(o => (
              <span key={o.key} title={o.title}
                style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:'0.78rem', fontWeight:700, color: ocorrencias[o.key] > 0 ? o.color : '#94a3b8', background: ocorrencias[o.key] > 0 ? `${o.color}18` : '#f1f5f9', border:`1px solid ${ocorrencias[o.key] > 0 ? o.color + '44' : '#e2e8f0'}`, borderRadius:20, padding:'4px 12px' }}>
                <i className={`bi ${o.icon}`} style={{ fontSize:'0.68rem' }} />
                {ocorrencias[o.key]}
              </span>
            ))}
            <span style={{ fontSize:'0.78rem', color: totalOcorrencias > 0 ? '#1e293b' : '#64748b', background: totalOcorrencias > 0 ? '#fef3c7' : '#f1f5f9', border:`1px solid ${totalOcorrencias > 0 ? '#fde68a' : '#e2e8f0'}`, borderRadius:8, padding:'4px 12px', fontWeight:700 }}>
              {totalOcorrencias} ocorrência(s)
            </span>
            <span style={{ fontSize:'0.78rem', color:'#64748b', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:8, padding:'4px 12px', fontWeight:600 }}>
              {registros.length} registro(s)
            </span>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="filter-bar mb-3" style={{ flexWrap:'wrap', gap:10 }}>

        {/* Setor */}
        <div style={{ minWidth:160 }}>
          <label className="form-label">Setor</label>
          <select className="form-select" value={filtroDep} onChange={e => { setFiltroDep(e.target.value); if (funcSel && e.target.value && String(funcSel.departamento) !== e.target.value) limparSelecao(); }}>
            <option value="">Todos</option>
            {deptos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>

        {/* Funcionário — searchable */}
        <div style={{ flex:2, minWidth:220 }} ref={dropRef}>
          <label className="form-label">Funcionário</label>
          <div style={{ position:'relative' }}>
            <input
              ref={inputRef}
              className="form-control"
              placeholder="Pesquisar por nome ou matrícula..."
              value={busca}
              onChange={e => { setBusca(e.target.value); setDropOpen(true); if (!e.target.value) setFuncSel(null); }}
              onFocus={() => setDropOpen(true)}
              autoComplete="off"
            />
            {funcSel && (
              <button onClick={limparSelecao} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:'1rem', padding:0 }}>
                <i className="bi bi-x-circle-fill" />
              </button>
            )}
            {dropOpen && funcsFiltradas.length > 0 && !funcSel && (
              <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 8px 24px rgba(0,0,0,0.10)', zIndex:100, maxHeight:220, overflowY:'auto' }}>
                {funcsFiltradas.slice(0,30).map(f => (
                  <div key={f.id} onMouseDown={() => selecionarFunc(f)}
                    style={{ padding:'7px 14px', cursor:'pointer', fontSize:'0.82rem', display:'flex', alignItems:'center', gap:8, borderBottom:'1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f0f7ff'}
                    onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background:'#eff6ff', border:'1px solid #bfdbfe', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.65rem', fontWeight:700, color:'#1d4ed8', flexShrink:0 }}>
                      {f.nome.split(' ').slice(0,2).map(p=>p[0]).join('').toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:600, color:'#1e293b' }}>{f.nome}</div>
                      <div style={{ fontSize:'0.68rem', color:'#94a3b8' }}>#{f.matricula}{f.departamento_nome ? ` · ${f.departamento_nome}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mês */}
        <div style={{ minWidth:130 }}>
          <label className="form-label">Mês</label>
          <select className="form-select" value={mes} onChange={e => setMes(Number(e.target.value))}>
            {MESES_PT.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>

        {/* Ano */}
        <div style={{ minWidth:90 }}>
          <label className="form-label">Ano</label>
          <select className="form-select" value={ano} onChange={e => setAno(Number(e.target.value))}>
            {anos.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div style={{ alignSelf:'flex-end' }}>
          <button
            onClick={funcSel ? gerarPDF : gerarPDFSetor}
            disabled={(!funcSel && !filtroDep) || loading || pdfLoading || (funcSel && registros.length === 0)}
            style={{
              ...btnBase,
              background: (funcSel || filtroDep) ? '#fef3c7' : '#f1f5f9',
              border: `1px solid ${(funcSel || filtroDep) ? '#fde68a' : '#e2e8f0'}`,
              color: (funcSel || filtroDep) ? '#92400e' : '#94a3b8',
              cursor: (funcSel || filtroDep) ? 'pointer' : 'not-allowed',
              padding: '6px 14px', fontSize: '0.78rem',
              opacity: pdfLoading ? 0.7 : 1,
            }}>
            {pdfLoading
              ? <><span className="spinner-border spinner-border-sm" style={{ width:'0.7rem', height:'0.7rem', borderWidth:'0.1em' }} />Gerando...</>
              : <><i className="bi bi-file-earmark-pdf-fill" />{funcSel ? 'Gerar PDF' : 'Gerar PDF do Setor'}</>}
          </button>
        </div>
      </div>

      {/* Card do funcionário selecionado */}

      {/* Estado vazio — sem seleção */}
      {!funcSel && (
        <div className="card-ponto">
          <div className="empty-state" style={{ padding:'48px 0' }}>
            <i className="bi bi-person-badge" style={{ fontSize:'2.5rem', opacity:0.15, color:'var(--primary)' }} />
            <p style={{ fontWeight:600, marginTop:8 }}>Selecione um funcionário</p>
            <p style={{ fontSize:'0.82rem' }}>Pesquise pelo nome ou matrícula no campo acima</p>
          </div>
        </div>
      )}

      {/* Tabela */}
      {funcSel && (
        <div className="card-ponto p-0">
          <div style={{ overflowX:'auto' }}>
            <table className="table-ponto" style={{ width:'100%', fontSize:'0.65rem', tableLayout:'fixed' }}>
              <colgroup>
                <col style={{ width:90 }} />
                <col style={{ width:36 }} />
                {[...Array(8)].map((_,i) => <col key={i} style={{ width:52 }} />)}
                <col style={{ width:60 }} />
                <col style={{ width:60 }} />
                <col style={{ width:65 }} />
                <col style={{ width:78 }} />
                <col style={{ width:52 }} />
                <col style={{ width:52 }} />
                <col style={{ width:130 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ paddingLeft:50 }}>Data</th>
                  <th style={{ textAlign:'center' }}>Dia</th>
                  <th style={{ textAlign:'center', color:'#16a34a' }}>Ent. 1</th>
                  <th style={{ textAlign:'center', color:'#dc2626' }}>Saí. 1</th>
                  <th style={{ textAlign:'center', color:'#16a34a' }}>Ent. 2</th>
                  <th style={{ textAlign:'center', color:'#dc2626' }}>Saí. 2</th>
                  <th style={{ textAlign:'center', color:'#16a34a' }}>Ent. 3</th>
                  <th style={{ textAlign:'center', color:'#dc2626' }}>Saí. 3</th>
                  <th style={{ textAlign:'center', color:'#16a34a' }}>Ent. 4</th>
                  <th style={{ textAlign:'center', color:'#dc2626' }}>Saí. 4</th>
                  <th style={{ textAlign:'center', color:'#1d4ed8' }}>Trab.</th>
                  <th style={{ textAlign:'center', color:'#0891b2' }} title="Compensações aprovadas">Comp.</th>
                  <th style={{ textAlign:'center', color:'#7c3aed' }}>Extra</th>
                  <th style={{ textAlign:'center', color:'#64748b' }} title="Saldo do dia">Saldo</th>
                  <th style={{ textAlign:'center', color:'#0891b2' }} title="Período manhã: Saí.1 − Ent.1">Per.</th>
                  <th style={{ textAlign:'center', color:'#a855f7' }} title="Almoço: Ent.2 − Saí.1">Alm.</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={15} className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary" /></td></tr>
                )}
                {!loading && grade.map(({ iso, diaSem, reg }, i) => {
                  const [yy, mm, dd] = iso.split('-');
                  const dataBR = `${dd}/${mm}/${yy}`;
                  const fds = diaSem === 'Sáb' || diaSem === 'Dom';
                  const compMins = compPorData[iso];
                  const status = reg?.status || null;
                  const cor = status ? (STATUS_COR[status] || STATUS_COR.NORMAL) : null;

                  const almocoTardio    = reg?.almoco_tardio    || false;
                  const almocoPrecoce   = (reg?.almoco_precoce || false) && !almocoTardio;
                  const almocoCurto     = reg?.almoco_curto     || false;
                  const saidaApos1815   = reg?.saida_apos_1815   || false;
                  const entradaAtrasada = reg?.entrada_atrasada  || false;
                  const horarios = [
                    reg?.hora_entrada, reg?.hora_saida_almoco,
                    reg?.hora_retorno_almoco, reg?.hora_saida,
                    reg?.hora_entrada3, reg?.hora_saida3,
                    reg?.hora_entrada4, reg?.hora_saida4,
                  ];
                  const ultimaSaidaIdx = [7, 5, 3].find(idx => horarios[idx]);

                  return (
                    <tr key={iso} style={{ background: fds ? '#fafbff' : i%2===0 ? '#fff' : '#f8fafc', opacity: !reg && !fds ? 0.55 : 1 }}>
                      <td style={{ fontWeight:600, color:'#1e293b', paddingLeft:50 }}>{dataBR}</td>
                      <td style={{ textAlign:'center', fontWeight: fds ? 700 : 500, color: fds ? '#dc2626' : '#64748b' }}>{diaSem}</td>
                      {horarios.map((h, idx) => {
                        const destacarEntrada = entradaAtrasada && idx === 0;
                        const destacarAlmoco  = almocoTardio   && idx === 1;
                        const destacarPrecoce = almocoPrecoce  && idx === 1;
                        const destacarCurto   = almocoCurto    && idx === 2;
                        const destacarSaida   = saidaApos1815  && idx === ultimaSaidaIdx;
                        const bgCell = destacarEntrada ? 'rgba(234,179,8,0.12)'
                                     : destacarAlmoco  ? 'rgba(251,146,60,0.12)'
                                     : destacarPrecoce ? 'rgba(59,130,246,0.10)'
                                     : destacarCurto   ? 'rgba(168,85,247,0.10)'
                                     : destacarSaida   ? 'rgba(239,68,68,0.10)'
                                     : undefined;
                        const icone =
                          (idx === 0 && entradaAtrasada)  ? { cls:'bi-alarm-fill',                color:'#ca8a04', title:'Entrada atrasada: mais de 15min após o horário previsto' } :
                          (idx === 1 && almocoTardio)     ? { cls:'bi-exclamation-triangle-fill', color:'#f97316', title:'Almoço tardio: mais de 5h após a entrada' } :
                          (idx === 1 && almocoPrecoce)    ? { cls:'bi-clock-fill',                color:'#3b82f6', title:'Almoço precoce: menos de 4h após a entrada' } :
                          (idx === 2 && almocoCurto)      ? { cls:'bi-cup-hot-fill',              color:'#a855f7', title:'Almoço curto: menos de 1h de intervalo' } :
                          (destacarSaida)                 ? { cls:'bi-moon-stars-fill',           color:'#dc2626', title:'Saída após 18h15' } : null;
                        return (
                          <td key={idx} style={{ textAlign:'center', fontVariantNumeric:'tabular-nums', background: bgCell }}>
                            {h ? (
                              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                                {icone && (
                                  <i className={`bi ${icone.cls}`} title={icone.title}
                                    style={{ color:icone.color, fontSize:'0.7rem' }} />
                                )}
                                <span style={{ color: idx % 2 === 0 ? '#16a34a' : '#dc2626', fontWeight:500 }}>
                                  {h.slice(0,5)}
                                </span>
                              </div>
                            ) : (
                              <span style={{ color:'#cbd5e1' }}>—</span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{ textAlign:'center', fontWeight:700, color:'#1d4ed8', fontVariantNumeric:'tabular-nums' }}>
                        {reg?.horas_trabalhadas != null ? decToHHMM(reg.horas_trabalhadas) : '—'}
                      </td>
                      <td style={{ textAlign:'center' }}>
                        {compMins
                          ? <span style={{ background:'#e0f2fe', color:'#0369a1', borderRadius:3, padding:'1px 6px', fontSize:'0.65rem', fontWeight:700 }}>{decToHHMM(compMins/60)}</span>
                          : <span style={{ color:'#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ textAlign:'center' }}>
                        {reg?.horas_extras != null && reg.horas_extras !== 0
                          ? <span style={{ background: reg.horas_extras>0 ? '#f3e8ff':'#fee2e2', color: reg.horas_extras>0 ? '#6b21a8':'#991b1b', borderRadius:3, padding:'1px 6px', fontSize:'0.65rem', fontWeight:700 }}>
                              {reg.horas_extras>0?'+':''}{decToHHMM(reg.horas_extras)}
                            </span>
                          : '—'}
                      </td>
                      <td style={{ textAlign:'center' }}>
                        {reg?.saldo_horas != null && Math.round(Math.abs(reg.saldo_horas)*60) <= 10
                          ? <i className="bi bi-check-circle-fill" title="Dentro da tolerância (±10 min)" style={{ fontSize:'0.72rem', color:'#16a34a' }} />
                          : reg?.saldo_horas != null && Math.round(Math.abs(reg.saldo_horas)*60) > 10
                            ? <span style={{ background: reg.saldo_horas>0 ? '#dcfce7':'#fee2e2', color: reg.saldo_horas>0 ? '#166534':'#991b1b', borderRadius:3, padding:'1px 6px', fontSize:'0.65rem', fontWeight:700 }}>
                                {reg.saldo_horas>0?'+':''}{decToHHMM(reg.saldo_horas)}
                              </span>
                            : '—'}
                      </td>
                      {(() => {
                        const toMin = t => { if (!t) return null; const [h, m] = t.slice(0,5).split(':').map(Number); return h * 60 + m; };
                        const fromMin = m => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`;
                        const e1 = toMin(reg?.hora_entrada);
                        const s1 = toMin(reg?.hora_saida_almoco);
                        const e2 = toMin(reg?.hora_retorno_almoco);
                        const perDiff = (e1 !== null && s1 !== null) ? s1 - e1 : null;
                        const almDiff = (s1 !== null && e2 !== null) ? e2 - s1 : null;
                        const renderCol = (diff, curtoMin, longoMin, colorCurto, colorLongo, iconCurto, iconLongo, titleCurto, titleLongo) => {
                          if (diff === null || diff <= 0) return <span style={{ color:'#cbd5e1' }}>—</span>;
                          const curto = diff < curtoMin;
                          const longo = diff > longoMin;
                          return (
                            <span style={{ fontVariantNumeric:'tabular-nums', fontWeight:500, fontSize:'0.65rem', color: curto ? colorCurto : longo ? colorLongo : '#64748b', display:'inline-flex', alignItems:'center', gap:2 }}>
                              {curto && <i className={`bi ${iconCurto}`} title={titleCurto} style={{ fontSize:'0.58rem' }} />}
                              {longo && <i className={`bi ${iconLongo}`} title={titleLongo} style={{ fontSize:'0.58rem' }} />}
                              {fromMin(diff)}
                            </span>
                          );
                        };
                        return (
                          <>
                            <td style={{ textAlign:'center' }}>
                              {renderCol(perDiff, 240, 300, '#3b82f6', '#f97316', 'bi-arrow-down-circle-fill', 'bi-arrow-up-circle-fill', 'Período curto: menos de 4h', 'Período longo: mais de 5h')}
                            </td>
                            <td style={{ textAlign:'center' }}>
                              {renderCol(almDiff, 60, 65, '#a855f7', '#f97316', 'bi-cup-hot-fill', 'bi-hourglass-split', 'Almoço curto: menos de 1h', 'Almoço longo: mais de 1h05')}
                            </td>
                          </>
                        );
                      })()}
                      <td>
                        {cor
                          ? <span style={{ background:cor.bg, color:cor.color, borderRadius:3, padding:'1px 7px', fontSize:'0.65rem', fontWeight:700, whiteSpace:'nowrap' }}>{reg?.status_display || status}</span>
                          : fds
                            ? <span style={{ fontSize:'0.62rem', color:'#cbd5e1' }}>—</span>
                            : <span style={{ fontSize:'0.62rem', color:'#cbd5e1' }}>Sem registro</span>
                        }
                      </td>
                    </tr>
                  );
                })}

                {/* Linha de totais */}
                {!loading && funcSel && (
                  <tr style={{ background:'#f0f4ff', borderTop:'2px solid #bfdbfe', fontWeight:700 }}>
                    <td colSpan={2} style={{ paddingLeft:50, fontSize:'0.68rem', color:'#475569', fontWeight:700 }}>TOTAL DO MÊS</td>
                    <td colSpan={8} />

                    <td style={{ textAlign:'center', color:'#1d4ed8', fontVariantNumeric:'tabular-nums' }}>{decToHHMM(totais.trab)}</td>
                    <td />
                    <td style={{ textAlign:'center' }}>
                      {totais.extra !== 0
                        ? <span style={{ color: totais.extra>0 ? '#6b21a8':'#991b1b' }}>{totais.extra>0?'+':''}{decToHHMM(totais.extra)}</span>
                        : '—'}
                    </td>
                    <td style={{ textAlign:'center' }}>
                      <span style={{ color: totais.saldo>=0 ? '#166534':'#991b1b' }}>
                        {totais.saldo>0?'+':''}{decToHHMM(totais.saldo)}
                      </span>
                    </td>
                    <td />
                    <td />
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
